import React, { useEffect, useRef, useState } from 'react'

import { faDownload, faLock, faLockOpen, faPlus, faRedo } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Popconfirm, Tooltip } from 'antd'
import { useSelector } from 'react-redux'

import { downloadFile } from '../../utils'
import { getInternshipConvention, unlockInternshipDocument, updateInternshipDocument } from '../../utils/api/internshipDocument'
import { selectUser } from '../../reducers/UserReducer'
import { onError, onSuccess } from '../../utils/apiHelper'
import { isFileTooLarge, notifyFileTooLarge } from '../../utils/files'
import { uploadInternshipConvention } from '../../utils/api/internship'
import { ACCEPTED_DOCUMENT_META } from '../../utils/entities/internshipDocument'
import { isHospitalAdmin, isObserver, isSchoolUser } from '../../utils/roles'
import { selectTranslate } from '../../reducers/LocaleReducer/LocalReducer'

const ConventionHandler = ({ internship }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [document, setDocument] = useState(null)
  const [uploading, setUploading] = useState(false)

  const uploaderRef = useRef(null)

  const canManageConvention = isHospitalAdmin(user) || isSchoolUser(user)

  useEffect(() => {
    if (internship && user) {
      getInternshipConvention(internship, user).then(setDocument)
    } else {
      setDocument(null)
    }
  }, [internship, user])

  const handleDownload = () => {
    downloadFile('/internship/document/download/' + document.id, document.originalName, user)
  }

  const refreshDocument = () => getInternshipConvention(internship, user).then(setDocument)

  const handleLock = () => {
    updateInternshipDocument(document, user).then(refreshDocument)
  }

  const handleUnlock = () => {
    unlockInternshipDocument(document, user).then(() => {
      refreshDocument()
      onSuccess(t('convention_handler.unlock.success'), 'bottomRight')
    })
  }

  const handleUpload = e => {
    const file = e.target.files[0]

    if (file && isFileTooLarge(file)) {
      notifyFileTooLarge(file, t)

      return
    }

    if (file && ACCEPTED_DOCUMENT_META.indexOf(file.type) > -1) {
      setUploading(true)

      uploadInternshipConvention(internship, file, user).then(json => {
        setDocument(json.data)
        setUploading(false)

        onSuccess(t('Internship agreement sent!'), 'bottomRight')
      })
    } else {
      onError(t('Only PDF, Word, Excel and CSV file types are supported.'), 'bottomRight')
    }
  }

  const renderLockButton = () => {
    if (document.locked && document.unlockable) {
      return (
        <Popconfirm
          placement='top'
          title={t('convention_handler.unlock.confirm')}
          okText={t('convention_handler.unlock.ok')}
          onConfirm={handleUnlock}
          cancelText={t('Cancel')}
        >
          <Tooltip placement='bottom' title={t('convention_handler.unlock.tooltip')}>
            <Button type='default' size='small'>
              <FontAwesomeIcon icon={faLock} />
            </Button>
          </Tooltip>
        </Popconfirm>
      )
    }

    if (document.locked) {
      return (
        <Tooltip
          placement='top'
          title={t(
            'This internship convention has been locked, the student cannot edit it anymore.'
          )}
        >
          <Button type='default' size='small' disabled>
            <FontAwesomeIcon icon={faLock} />
          </Button>
        </Tooltip>
      )
    }

    if (!canManageConvention) {
      return null
    }

    return (
      <Popconfirm
        placement='top'
        title={t('Accept and definitively lock this internship convention ?')}
        okText={t('Accept and lock')}
        onConfirm={handleLock}
        cancelText={t('Cancel')}
      >
        <Tooltip
          placement='bottom'
          title={t(
            'Accept and lock this internship convention to avoid the student to edit it.'
          )}
        >
          <Button type='default' size='small'>
            <FontAwesomeIcon icon={faLockOpen} />
          </Button>
        </Tooltip>
      </Popconfirm>
    )
  }

  return (
    <div className='convention-handler'>
      {internship && (
        <div className='flex-row'>
          <Button
            type='default'
            size='small'
            onClick={handleDownload}
            disabled={!document}
          >
            <FontAwesomeIcon icon={faDownload} />
            {t('common.download')}
          </Button>
          {!!document && !isObserver(user) && (
            <>
              <div className='h-spacing' />
              {renderLockButton()}
            </>
          )}
        </div>
      )}
      {internship && canManageConvention && (
        <>
          <Button
            disabled={document && document.locked}
            loading={uploading}
            size='small'
            style={{ marginTop: '5px' }}
            type='primary'
            onClick={() => uploaderRef.current.click()}
          >
            <FontAwesomeIcon icon={document ? faRedo : faPlus} /> &nbsp; {t(document ? 'common.update' : 'common.add')}
          </Button>
          <input type='file' ref={uploaderRef} onChange={handleUpload} accept={ACCEPTED_DOCUMENT_META.join(',')} style={{ display: 'none' }} />
        </>
      )}
    </div>
  )
}

export default ConventionHandler
